import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useRemoveUserFromSubscription } from 'src/api/queries';
import { Button, Modal, NotificationBox } from 'src/components';

export const RemoveUserFromSubscriptionModal = ({
  userId,
  userEmail,
  onSuccess,
}: {
  userId: string;
  userEmail: string;
  onSuccess?: () => void;
}) => {
  const removeUserFromSubscription = useRemoveUserFromSubscription();

  return (
    <Modal.Content>
      <Modal.Title asChild>
        <Typography variant="heading-md" className="mb-24">
          Remove member
        </Typography>
      </Modal.Title>

      <Typography variant="body-md" className="mb-16 whitespace-normal">
        Are you sure you want to remove{' '}
        <span data-cy="member-email">{userEmail}</span>?
      </Typography>

      <Modal.Description asChild>
        <Typography variant="body-md" className="my-16">
          This member will keep access to their files, but lose access to all
          subscription features and benefits. If they had a free trial before
          joining this subscription, they won&apos;t be able to resume their
          trial if you remove them.
        </Typography>
      </Modal.Description>

      {removeUserFromSubscription.isError && (
        <NotificationBox
          className="my-16"
          error={removeUserFromSubscription.error}
          title="Something went wrong"
        >
          Something went wrong while removing the member from the seat. Please
          try again or contact support if the problem persists.
        </NotificationBox>
      )}

      <div className="mt-24 flex flex-col flex-wrap justify-center gap-16 ls:flex-row">
        <Modal.Close asChild>
          <Button
            className="w-full ls:w-fit"
            variant="secondary"
            analytics={{
              text: 'Keep member',
              location: ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
              action: 'keep member (close modal)',
            }}
          >
            Keep member
          </Button>
        </Modal.Close>

        <Button
          className="w-full ls:w-fit"
          loading={removeUserFromSubscription.isPending}
          disabled={removeUserFromSubscription.isPending}
          data-cy="confirm-remove-user"
          onClick={() => {
            if (!userId) return;

            removeUserFromSubscription.mutate(
              { subscriptionRoleId: userId },
              { onSuccess }
            );
          }}
          analytics={{
            text: 'Remove member',
            location: ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
            action: 'remove member',
          }}
        >
          Remove member
        </Button>
      </div>
    </Modal.Content>
  );
};
